/**
 * Two ways CosMon hears about a camera NX records (070), said apart.
 *
 * A camera LINKED to NX has CosMon's own connection (through the site's
 * router) and NX's view (from the NX server on site). They disagree exactly
 * when it matters — the camera answers CosMon but NX cannot reach it, so
 * nothing is recording — and one "reachable" line cannot say which is which
 * (owner, 2026-09-30: "its not clear what nx is saying and what direct
 * connection is saying"). A camera ADDED from NX has only NX's view.
 */

import type { CellState } from './fleet-state.ts';

/** The pill's states (components/Pill.tsx), named from their plain-TS source. */
type PillState = CellState | 'neutral';

/** What `/api/assets/:id/detail` says of NX for this camera. */
export interface NxView {
  server_id: string;
  server_label: string;
  /** Linked: reached its own way too. Otherwise added from NX: NX only. */
  linked: boolean;
  /** Null when NX's latest list no longer has this camera. */
  name: string | null;
  status: string | null;
  recording: string | null;
  reported_at: string | null;
  /** On a failover server (072): the NX server it is normally on; null when it is there. */
  failover_home?: string | null;
}

export interface SourceLine {
  state: PillState;
  /** A few words, for the pill. */
  headline: string;
  /** What it means, or why, when there is more to say. */
  detail: string | null;
}

interface Reading {
  outcome: 'pass' | 'fail' | 'unknown' | 'unavailable';
  reason: string | null;
}

/** CosMon's own connection to the camera. */
export function directLine(nx: NxView, reachable: Reading | undefined): SourceLine {
  if (!nx.linked) {
    return { state: 'neutral', headline: 'None', detail: 'CosMon reaches this camera only through NX.' };
  }
  if (reachable === undefined) return { state: 'stale', headline: 'Not checked yet', detail: null };
  switch (reachable.outcome) {
    case 'pass':
      return { state: 'ok', headline: 'Answering', detail: null };
    case 'fail':
      return { state: 'fault', headline: 'Not answering', detail: reachable.reason };
    case 'unknown':
      return {
        state: 'stale',
        headline: 'No answer',
        detail: `The connection attempt failed${reachable.reason !== null ? `: ${reachable.reason}` : ''}. Not counted as down — the path may be the problem, not the camera.`,
      };
    default:
      return { state: 'unmonitored', headline: 'Cannot be checked', detail: reachable.reason };
  }
}

/** NX's view of the camera, from its NX server's latest report. */
export function nxLine(nx: NxView): SourceLine {
  if (nx.name === null || nx.status === null) {
    return {
      state: 'stale',
      headline: 'Not listed',
      detail: nx.reported_at === null ? 'The NX server has not reported yet.' : 'NX no longer lists this camera.',
    };
  }
  switch (nx.status) {
    case 'Recording':
      return { state: 'ok', headline: 'Recording', detail: null };
    case 'Online':
      return nx.recording === 'On'
        ? { state: 'fault', headline: 'Online, not recording', detail: 'NX has recording on for it, and is not recording.' }
        : { state: 'unmonitored', headline: 'Online', detail: 'Recording is off for this camera in NX.' };
    case 'Offline':
      return { state: 'fault', headline: 'Offline', detail: 'NX cannot reach this camera, so it is not recording it.' };
    case 'Unauthorized':
      return { state: 'fault', headline: 'Cannot log in', detail: 'NX reaches the camera but its login is refused, so it is not recording it.' };
    default:
      return { state: 'warning', headline: nx.status, detail: null };
  }
}

/** The check table's names, saying where each answer came from. */
export function sourcedLabel(checkKey: string, label: string, nx: NxView | null): string {
  if (nx === null) return label;
  if (checkKey === 'reachable') return nx.linked ? 'Reachable (direct)' : 'Reachable (via NX)';
  if (checkKey === 'nx_camera_recording') return 'Recording (NX)';
  return label;
}
